<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Analyte Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading :title="header" class="mb-3">
      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <button
              v-show="checkPermission('delete analyte')"
              title="Delete analyte"
              class="btn btn-danger ml-1"
              @click="showDeleteModal"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read analyte'">
      <template #gated-content>
        <analyte-card v-if="analyte_id" :analyte="analyte" />
        <analyte-detail-cardstack />
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import AnalyteCard from "@/components/lab/analyte/AnalyteCard";
import AnalyteDetailCardstack from "@/components/lab/analyte/AnalyteDetailCardstack";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { analyteNotifications } from "@/shared/notifications";
import { getModel } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { notificationOptions } from "@/shared/toast-notification-options";

Vue.use(VueNotifications, notificationOptions);

const AnalyteRepository = LabRepositoryFactory.get("analyte");

export default {
  name: "AnalyteDetail",
  components: {
    AnalyteCard,
    AnalyteDetailCardstack,
    BasicHeading,
    HeaderButtonGroup,
    PermissionAlertGate,
  },
  mixins: [ToastNotifications],
  data() {
    return {
      loading: true,
      analyte_id: this.$route.params.analyte_id || null,
      analyte: null,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: "/analytes",
            title: "Analyte list",
          },
          {
            icon: "fa-pencil",
            linkto: `/analytes/${this.analyte_id}/edit`,
            title: "Edit Analyte",
            variant: "warning",
            permission: this.checkPermission("edit analyte"),
          },
        ],
        dropdown: {
          items: [
            {
              icon: "fa-search",
              linkto: `/analytes/${this.analyte_id}/audit-trail`,
              text: "Audit Trail",
              variant: "info",
            },
            {
              icon: "fa-chart-bar",
              linkto: `/analytes/${this.analyte_id}/range`,
              text: "Analyte Ranges",
              variant: "info",
            }
          ]
        }
      };
    },
    sample_type() {
      return this.analyte.sample_type ? this.analyte.getSampleType() : null;
    },
    header() {
      return `${
        this.sample_type ? this.sample_type.name : "Sample Type not specified"
      }: ${this.analyte.name}`;
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this analyte. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await AnalyteRepository.suspend(this.analyte_id);

              this.deleteSuccess();
              this.$router.push("/analytes");
            } catch ({ response }) {
              const { status, data } = response.response;

              this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
                title: "Server Error.",
                variant: "danger",
              });
            }
          }
        });
    },
  },
  async created() {
    this.loading = true;

    this.analyte = await getModel({
      model_type: "analyte",
      id: this.analyte_id,
      message: this.$bvToast,
    });

    this.analyte ? (this.loading = false) : this.$router.back();
  },
  notifications: analyteNotifications,
};
</script>
